import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, addToCartAsync } from "../../Features/Cart/cartSlice";
import { ToastContainer, toast } from "react-toastify";
import {
  RiShoppingCartLine,
  RiFilterLine,
  RiArrowUpDownLine,
  RiSearchLine,
} from "react-icons/ri";
import "./ShopByBrand.css";
import { Link } from "react-router-dom";

const ShopByBrand = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.user?._id);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [sortBy, setSortBy] = useState("name-asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const cartItems = useSelector((state) => state.cart.items);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get("https://api.swasthmitra.com/api/medicine");
        setProducts(response.data);
        const uniqueBrands = [
          ...new Set(response.data.map((product) => product.brand)),
        ].filter(Boolean);
        setBrands(uniqueBrands);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddToCart = async (product) => {
    if (!userId) {
      toast.error("Please login to add items to cart");
      return;
    }
    const cartItem = {
      productID: product._id,
      productName: product.name,
      dealRate: parseFloat(product.salePrice),
      productImage: product.imageUrl || product.imageUrls[0] || "",
      quantity: 1,
      batchNumber: product.batchNumber,
      brand: product.brand,
      packOf: product.packOf,
      gst: product.gst,
      mrp: product.mrp,
      expiryDate: product.expiryDate,
    };

    try {
      await dispatch(
        addToCartAsync({
          userId,
          productID: product._id,
          quantity: 1,
        })
      );
      dispatch(addToCart(cartItem));
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error("Failed to add item to cart");
    }
  };

  // Search handler
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Sort menu handler
  const handleSort = (sortType) => {
    setSortBy(sortType);
    setShowSortMenu(false);
  };

  // Filter handlers
  const handleBrandFilter = (brand) => {
    setSelectedBrand(brand);
    setShowFilters(false);
  };

  const handlePriceRangeFilter = (min, max) => {
    setPriceRange([min, max]);
  };

  const filteredAndSortedProducts = products
    .filter((product) => {
      const matchesSearch = product.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      const matchesBrand = !selectedBrand || product.brand === selectedBrand;

      const matchesPrice =
        product.dealRate >= priceRange[0] && product.dealRate <= priceRange[1];

      return matchesSearch && matchesBrand && matchesPrice;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "price-asc":
          return a.dealRate - b.dealRate;
        case "price-desc":
          return b.dealRate - a.dealRate;
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

  return (
    <div className="shop-by-brand">
      <ToastContainer />

      <div className="search-container">
        <RiSearchLine />
        <input
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={handleSearch}
        />
      </div>

      <div className="header-controls">
        <div className="sort-dropdown">
          <button
            className="sort-button"
            onClick={() => setShowSortMenu(!showSortMenu)}
          >
            <RiArrowUpDownLine />
            Sort By
          </button>
          {showSortMenu && (
            <div className="sort-menu">
              <button onClick={() => handleSort("name-asc")}>Name (A-Z)</button>
              <button onClick={() => handleSort("name-desc")}>
                Name (Z-A)
              </button>
              <button onClick={() => handleSort("price-asc")}>
                Price (Low to High)
              </button>
              <button onClick={() => handleSort("price-desc")}>
                Price (High to Low)
              </button>
            </div>
          )}
        </div>

        <div className="filter-dropdown">
          <button
            className="filters-button"
            onClick={() => setShowFilters(!showFilters)}
          >
            <RiFilterLine />
            Filters
          </button>
          {showFilters && (
            <div className="filter-menu">
              <div className="filter-section brands">
                <h4>Brands</h4>
                <div className="brands-list">
                  <button
                    className={!selectedBrand ? "active" : ""}
                    onClick={() => handleBrandFilter(null)}
                  >
                    All Brands
                  </button>
                  {brands.map((brand) => (
                    <button
                      key={brand}
                      className={selectedBrand === brand ? "active" : ""}
                      onClick={() => handleBrandFilter(brand)}
                    >
                      {brand}
                    </button>
                  ))}
                </div>
              </div>
              <div className="filter-section">
                <h4>Price Range</h4>
                <div className="price-range-inputs">
                  <input
                    type="number"
                    value={priceRange[0]}
                    onChange={(e) =>
                      handlePriceRangeFilter(
                        parseInt(e.target.value),
                        priceRange[1]
                      )
                    }
                    placeholder="Min"
                  />
                  <span>to</span>
                  <input
                    type="number"
                    value={priceRange[1]}
                    onChange={(e) =>
                      handlePriceRangeFilter(
                        priceRange[0],
                        parseInt(e.target.value)
                      )
                    }
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="products-grid">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          filteredAndSortedProducts.map((product) => (
            <motion.div
              key={product._id}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="product-card"
            >
              <div className="product-image">
                <img
                  src={
                    product.imageUrl ||
                    product.imageUrls[0] ||
                    "/default-medicine.png"
                  }
                  alt={product.name}
                  loading="lazy"
                />
              </div>
              <div className="product-info">
                <h3 className="product-title">{product.name}</h3>
                <div className="product-price">₹{product.dealRate}</div>
                <button
                  className={`view-details-btn ${
                    cartItems.some((item) => item.productID === product._id)
                      ? "disabled"
                      : ""
                  }`}
                  onClick={() => {
                    if (
                      !cartItems.some((item) => item.productID === product._id)
                    ) {
                      handleAddToCart(product);
                    }
                  }}
                >
                  {cartItems.some((item) => item.productID === product._id)
                    ? "In Cart"
                    : "Add to Cart"}
                </button>
                <Link to={`/product/${product._id}`}>
                  <button className="view-details-btn">View Details</button>
                </Link>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <div className="pagination">
        <button>Previous</button>
        <button className="active">1</button>
        <button>Next</button>
      </div>
    </div>
  );
};

export default ShopByBrand;
